import React, { useState, useEffect, useCallback } from 'react'
import { Table } from 'react-bootstrap'
import { FaSearch } from 'react-icons/fa'
import API from '../../../service/api'
import { customToast } from '../../../utils/toasts'
import './PushApiConfig.css'

const DATA_TYPES = ['string', 'number', 'boolean', 'Date_Epoch_Milliseconds', 'Date_ISO', 'Date_DD/MM/YYYY HH:mm:ss']

const emptyDpmEntry = { key: '', value: '', dataType: 'string', valueRange: '', valueDecimalPlacesUpTo: 0, isMandatory: false }

const emptyAddForm = {
  clientCode: '',
  serviceId: 'PS',
  status: 'Published',
  isActive: true,
  allowedVehicleNo: 'all',
  'apiConfigs.clientApiUrl': '',
  'apiConfigs.clientApiMethod': 'POST',
  'apiConfigs.clientApiPayloadHeadJsonString': '',
  'apiConfigs.isCountIncludedInPH': false,
  'apiConfigs.phKeyForCount': '',
  'apiConfigs.isDataPointsMapInsidePH': true,
  'apiConfigs.phKeyForDataPointsMap': '',
  'apiConfigs.maxDataObjectPerAPICall': 1000,
  'apiConfigs.maxHistoricDataObjectPerAPICall': 800,
  'apiConfigs.isHistoricalDataReq': false,
  'apiConfigs.resendFailedObjects': false,
  'apiConfigs.maxCanDataTimestampOldInDays': 90,
  'apiConfigs.nullifyCanPointsForCanNonComm': false
}

const PushApiConfig = () => {
  const [configs, setConfigs] = useState([])
  const [searchKey, setSearchKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [addForm, setAddForm] = useState({ ...emptyAddForm })
  const [editModal, setEditModal] = useState(null) // config object being edited
  const [dpmModal, setDpmModal] = useState(null) // config object for dataPointsMap editing
  const [editForm, setEditForm] = useState({})
  const [newDpmEntry, setNewDpmEntry] = useState(null) // inline add row
  const [editDpmEntry, setEditDpmEntry] = useState(null) // { entryId, ...fields }

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true)
      const res = await API({ url: '/push-api-config', method: 'GET', manageLoader: false })
      setConfigs(res?.data?.data || [])
    } catch (err) {
      console.error('Failed to fetch push api configs', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchConfigs() }, [fetchConfigs])

  const filtered = configs.filter(c => {
    if (!searchKey) return true
    const s = searchKey.toLowerCase()
    return (c.clientCode || '').toLowerCase().includes(s) ||
      (c.serviceId || '').toLowerCase().includes(s) ||
      (c.apiConfigs?.clientApiUrl || '').toLowerCase().includes(s)
  })

  // -- Edit Config Modal --
  const openEditModal = (config) => {
    setEditForm({
      clientCode: config.clientCode || '',
      serviceId: config.serviceId || '',
      status: config.status || '',
      isActive: config.isActive ?? true,
      allowedVehicleNo: (config.allowedVehicleNo || []).join(', '),
      'apiConfigs.clientApiUrl': config.apiConfigs?.clientApiUrl || '',
      'apiConfigs.clientApiMethod': config.apiConfigs?.clientApiMethod || 'POST',
      'apiConfigs.clientApiPayloadHeadJsonString': config.apiConfigs?.clientApiPayloadHeadJsonString || '',
      'apiConfigs.isCountIncludedInPH': config.apiConfigs?.isCountIncludedInPH ?? false,
      'apiConfigs.phKeyForCount': config.apiConfigs?.phKeyForCount || '',
      'apiConfigs.isDataPointsMapInsidePH': config.apiConfigs?.isDataPointsMapInsidePH ?? true,
      'apiConfigs.phKeyForDataPointsMap': config.apiConfigs?.phKeyForDataPointsMap || '',
      'apiConfigs.maxDataObjectPerAPICall': config.apiConfigs?.maxDataObjectPerAPICall || 1000,
      'apiConfigs.maxHistoricDataObjectPerAPICall': config.apiConfigs?.maxHistoricDataObjectPerAPICall || 800,
      'apiConfigs.isHistoricalDataReq': config.apiConfigs?.isHistoricalDataReq ?? false,
      'apiConfigs.resendFailedObjects': config.apiConfigs?.resendFailedObjects ?? false,
      'apiConfigs.maxCanDataTimestampOldInDays': config.apiConfigs?.maxCanDataTimestampOldInDays || 90,
      'apiConfigs.nullifyCanPointsForCanNonComm': config.apiConfigs?.nullifyCanPointsForCanNonComm ?? false
    })
    setEditModal(config)
  }

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }))
  }

  const saveEditConfig = async () => {
    try {
      const body = {}
      Object.entries(editForm).forEach(([k, v]) => {
        const keys = k.split('.')
        if (keys.length === 2) {
          if (!body[keys[0]]) body[keys[0]] = {}
          body[keys[0]][keys[1]] = v
        } else {
          body[k] = v
        }
      })
      // Convert allowedVehicleNo string to array
      body.allowedVehicleNo = body.allowedVehicleNo
        ? body.allowedVehicleNo.split(',').map(v => v.trim()).filter(Boolean)
        : ['all']
      const res = await API({ url: `/push-api-config/${editModal._id}`, method: 'PUT', data: body, manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: 'Config updated successfully' })
        setEditModal(null)
        fetchConfigs()
      }
    } catch (err) {
      console.error('Update failed', err)
    }
  }

  // -- DataPointsMap Modal --
  const openDpmModal = (config) => {
    setDpmModal(config)
    setNewDpmEntry(null)
    setEditDpmEntry(null)
  }

  const refreshDpmModal = async (configId) => {
    const res = await API({ url: `/push-api-config/${configId}`, method: 'GET', manageLoader: false })
    if (res?.data?.data) {
      setDpmModal(res.data.data)
      // also update in configs list
      setConfigs(prev => prev.map(c => c._id === configId ? res.data.data : c))
    }
  }

  const handleAddDpmEntry = async () => {
    if (!newDpmEntry?.key || !newDpmEntry?.value) {
      customToast({ message: 'Key and Value are required', type: 'error' })
      return
    }
    try {
      const entry = {
        ...newDpmEntry,
        valueRange: newDpmEntry.valueRange ? newDpmEntry.valueRange.split(',').map(v => v.trim()).filter(Boolean) : [],
        valueDecimalPlacesUpTo: Number(newDpmEntry.valueDecimalPlacesUpTo) || 0
      }
      const res = await API({ url: `/push-api-config/${dpmModal._id}/data-points-map`, method: 'POST', data: entry, manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: 'Data point added' })
        setNewDpmEntry(null)
        await refreshDpmModal(dpmModal._id)
      }
    } catch (err) {
      console.error('Add data point failed', err)
    }
  }

  const handleUpdateDpmEntry = async () => {
    if (!editDpmEntry?.key || !editDpmEntry?.value) {
      customToast({ message: 'Key and Value are required', type: 'error' })
      return
    }
    try {
      const entry = {
        key: editDpmEntry.key,
        value: editDpmEntry.value,
        dataType: editDpmEntry.dataType,
        valueRange: editDpmEntry.valueRange ? editDpmEntry.valueRange.split(',').map(v => v.trim()).filter(Boolean) : [],
        valueDecimalPlacesUpTo: Number(editDpmEntry.valueDecimalPlacesUpTo) || 0,
        isMandatory: editDpmEntry.isMandatory
      }
      const res = await API({ url: `/push-api-config/${dpmModal._id}/data-points-map/${editDpmEntry.entryId}`, method: 'PUT', data: entry, manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: 'Data point updated' })
        setEditDpmEntry(null)
        await refreshDpmModal(dpmModal._id)
      }
    } catch (err) {
      console.error('Update data point failed', err)
    }
  }

  const handleToggleConfigStatus = async (configId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate'
    if (!window.confirm(`Are you sure you want to ${action} this config?`)) return
    try {
      const res = await API({ url: `/push-api-config/${configId}/toggle-status`, method: 'PUT', manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: `Config ${action}d successfully` })
        fetchConfigs()
      }
    } catch (err) {
      console.error('Toggle status failed', err)
    }
  }

  // -- Add Config --
  const handleAddFormChange = (field, value) => {
    setAddForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCreateConfig = async () => {
    if (!addForm.clientCode) {
      customToast({ message: 'Client Code is required', type: 'error' })
      return
    }
    try {
      const body = {}
      Object.entries(addForm).forEach(([k, v]) => {
        const keys = k.split('.')
        if (keys.length === 2) {
          if (!body[keys[0]]) body[keys[0]] = {}
          body[keys[0]][keys[1]] = v
        } else {
          body[k] = v
        }
      })
      // Convert allowedVehicleNo string to array
      body.allowedVehicleNo = body.allowedVehicleNo
        ? body.allowedVehicleNo.split(',').map(v => v.trim()).filter(Boolean)
        : ['all']

      const res = await API({ url: '/push-api-config', method: 'POST', data: body, manageLoader: false })
      if (res?.data?.status) {
        customToast({ message: 'Config created successfully' })
        setAddModal(false)
        setAddForm({ ...emptyAddForm })
        fetchConfigs()
      } else {
        customToast({ message: res?.data?.message || 'Create failed', type: 'error' })
      }
    } catch (err) {
      console.error('Create config failed', err)
    }
  }

  const startEditDpmRow = (entry) => {
    setEditDpmEntry({
      entryId: entry._id,
      key: entry.key,
      value: entry.value,
      dataType: entry.dataType || 'string',
      valueRange: (entry.valueRange || []).join(', '),
      valueDecimalPlacesUpTo: entry.valueDecimalPlacesUpTo || 0,
      isMandatory: entry.isMandatory || false
    })
    setNewDpmEntry(null)
  }

  const activeCount = filtered.filter(c => c.isActive).length
  const inactiveCount = filtered.filter(c => !c.isActive).length

  return (
    <div className='pac-container'>
      <div className='pac-header'>
        <h5 className='pac-title'>Push API Config</h5>
        <div className='pac-stats'>
          <span className='pac-badge pac-badge-active'>{activeCount} Active</span>
          <span className='pac-badge pac-badge-inactive'>{inactiveCount} Inactive</span>
          <span className='pac-badge pac-badge-total'>{filtered.length} Total</span>
        </div>
      </div>

      <div className='pac-toolbar'>
        <div className='pac-search-box'>
          <FaSearch className='pac-search-icon' />
          <input
            type='text'
            placeholder='Search by client code, service ID, or URL...'
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className='pac-search-input'
          />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className='pac-action-btn pac-btn-add' style={{ padding: '6px 18px', fontSize: 13 }} onClick={() => { setAddForm({ ...emptyAddForm }); setAddModal(true) }}>
            + Add Config
          </button>
          <button className='pac-refresh-btn' onClick={fetchConfigs} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className='pac-table-wrapper'>
        <Table bordered hover size='sm' className='pac-table'>
          <thead>
            <tr>
              <th>S.No</th>
              <th>Client Code</th>
              <th>Service ID</th>
              <th>Status</th>
              <th>Active</th>
              <th>API URL</th>
              <th>Method</th>
              <th>Max Per Call</th>
              <th>Data Points</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && configs.length === 0 && (
              <tr><td colSpan={10} className='text-center'>Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10} className='text-center'>No configs found</td></tr>
            )}
            {filtered.map((c, i) => (
              <tr key={c._id}>
                <td>{i + 1}</td>
                <td><strong>{c.clientCode}</strong></td>
                <td>{c.serviceId}</td>
                <td>{c.status || '-'}</td>
                <td>
                  <span className={`pac-status ${c.isActive ? 'pac-status-active' : 'pac-status-inactive'}`}>
                    {c.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  title={c.apiConfigs?.clientApiUrl}>{c.apiConfigs?.clientApiUrl || '-'}</td>
                <td>{c.apiConfigs?.clientApiMethod || '-'}</td>
                <td>{c.apiConfigs?.maxDataObjectPerAPICall || '-'}</td>
                <td>{c.apiConfigs?.dataPointsMap?.length || 0}</td>
                <td>
                  <button className='pac-action-btn pac-btn-edit' onClick={() => openEditModal(c)}>Edit</button>
                  <button className='pac-action-btn pac-btn-map' onClick={() => openDpmModal(c)}>Data Map</button>
                  <button
                    className={`pac-action-btn ${c.isActive ? 'pac-btn-danger' : 'pac-btn-add'}`}
                    onClick={() => handleToggleConfigStatus(c._id, c.isActive)}
                  >
                    {c.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Add Config Modal */}
      {addModal && (
        <div className='pac-modal-overlay' onClick={() => setAddModal(false)}>
          <div className='pac-modal' onClick={e => e.stopPropagation()}>
            <div className='pac-modal-header'>
              <h6>Add New Push API Config</h6>
              <button className='pac-modal-close' onClick={() => setAddModal(false)}>&times;</button>
            </div>
            <div className='pac-modal-body'>
              <div className='pac-form-row'>
                <div className='pac-form-group'>
                  <label>Client Code *</label>
                  <input value={addForm.clientCode} onChange={e => handleAddFormChange('clientCode', e.target.value.toUpperCase())} placeholder='e.g. DELH0001' />
                </div>
                <div className='pac-form-group'>
                  <label>Service ID</label>
                  <input value={addForm.serviceId} onChange={e => handleAddFormChange('serviceId', e.target.value)} />
                </div>
              </div>
              <div className='pac-form-row'>
                <div className='pac-form-group'>
                  <label>Status</label>
                  <select value={addForm.status} onChange={e => handleAddFormChange('status', e.target.value)}>
                    <option value='Published'>Published</option>
                    <option value='Draft'>Draft</option>
                    <option value='Disabled'>Disabled</option>
                  </select>
                </div>
                <div className='pac-form-group'>
                  <label>Allowed Vehicles (comma-separated or "all")</label>
                  <input value={addForm.allowedVehicleNo} onChange={e => handleAddFormChange('allowedVehicleNo', e.target.value)} placeholder='all' />
                </div>
              </div>
              <div className='pac-form-group'>
                <label>Client API URL</label>
                <input value={addForm['apiConfigs.clientApiUrl']} onChange={e => handleAddFormChange('apiConfigs.clientApiUrl', e.target.value)} placeholder='https://...' />
              </div>
              <div className='pac-form-row'>
                <div className='pac-form-group'>
                  <label>API Method</label>
                  <select value={addForm['apiConfigs.clientApiMethod']} onChange={e => handleAddFormChange('apiConfigs.clientApiMethod', e.target.value)}>
                    <option value='POST'>POST</option>
                    <option value='PUT'>PUT</option>
                    <option value='PATCH'>PATCH</option>
                  </select>
                </div>
                <div className='pac-form-group'>
                  <label>Payload Head JSON String</label>
                  <input value={addForm['apiConfigs.clientApiPayloadHeadJsonString']} onChange={e => handleAddFormChange('apiConfigs.clientApiPayloadHeadJsonString', e.target.value)} />
                </div>
              </div>
              <div className='pac-form-row-3'>
                <div className='pac-form-group'>
                  <label>PH Key For DataPoints</label>
                  <input value={addForm['apiConfigs.phKeyForDataPointsMap']} onChange={e => handleAddFormChange('apiConfigs.phKeyForDataPointsMap', e.target.value)} />
                </div>
                <div className='pac-form-group'>
                  <label>Max Data Per Call</label>
                  <input type='number' value={addForm['apiConfigs.maxDataObjectPerAPICall']} onChange={e => handleAddFormChange('apiConfigs.maxDataObjectPerAPICall', Number(e.target.value))} />
                </div>
                <div className='pac-form-group'>
                  <label>Max Historic Per Call</label>
                  <input type='number' value={addForm['apiConfigs.maxHistoricDataObjectPerAPICall']} onChange={e => handleAddFormChange('apiConfigs.maxHistoricDataObjectPerAPICall', Number(e.target.value))} />
                </div>
              </div>
              <div className='pac-form-row-3'>
                <div className='pac-form-group'>
                  <label>Max Timestamp Old (days)</label>
                  <input type='number' value={addForm['apiConfigs.maxCanDataTimestampOldInDays']} onChange={e => handleAddFormChange('apiConfigs.maxCanDataTimestampOldInDays', Number(e.target.value))} />
                </div>
                <div className='pac-form-group'>
                  <label>PH Key For Count</label>
                  <input value={addForm['apiConfigs.phKeyForCount']} onChange={e => handleAddFormChange('apiConfigs.phKeyForCount', e.target.value)} />
                </div>
                <div className='pac-form-group'>
                  <label>&nbsp;</label>
                  <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
                    <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type='checkbox' className='pac-checkbox' checked={addForm['apiConfigs.isDataPointsMapInsidePH']}
                        onChange={e => handleAddFormChange('apiConfigs.isDataPointsMapInsidePH', e.target.checked)} />
                      Map Inside PH
                    </label>
                    <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type='checkbox' className='pac-checkbox' checked={addForm['apiConfigs.isHistoricalDataReq']}
                        onChange={e => handleAddFormChange('apiConfigs.isHistoricalDataReq', e.target.checked)} />
                      Historical
                    </label>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type='checkbox' className='pac-checkbox' checked={addForm['apiConfigs.isCountIncludedInPH']}
                    onChange={e => handleAddFormChange('apiConfigs.isCountIncludedInPH', e.target.checked)} />
                  Count in PH
                </label>
                <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type='checkbox' className='pac-checkbox' checked={addForm['apiConfigs.resendFailedObjects']}
                    onChange={e => handleAddFormChange('apiConfigs.resendFailedObjects', e.target.checked)} />
                  Resend Failed
                </label>
                <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type='checkbox' className='pac-checkbox' checked={addForm['apiConfigs.nullifyCanPointsForCanNonComm']}
                    onChange={e => handleAddFormChange('apiConfigs.nullifyCanPointsForCanNonComm', e.target.checked)} />
                  Nullify CAN Non-Comm
                </label>
              </div>
            </div>
            <div className='pac-modal-footer'>
              <button className='pac-btn-cancel' onClick={() => setAddModal(false)}>Cancel</button>
              <button className='pac-btn-save' onClick={handleCreateConfig}>Create Config</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Config Modal */}
      {editModal && (
        <div className='pac-modal-overlay' onClick={() => setEditModal(null)}>
          <div className='pac-modal' onClick={e => e.stopPropagation()}>
            <div className='pac-modal-header'>
              <h6>Edit Config - {editModal.clientCode}</h6>
              <button className='pac-modal-close' onClick={() => setEditModal(null)}>&times;</button>
            </div>
            <div className='pac-modal-body'>
              <div className='pac-form-row'>
                <div className='pac-form-group'>
                  <label>Client Code</label>
                  <input value={editForm.clientCode} onChange={e => handleEditFormChange('clientCode', e.target.value)} />
                </div>
                <div className='pac-form-group'>
                  <label>Service ID</label>
                  <input value={editForm.serviceId} onChange={e => handleEditFormChange('serviceId', e.target.value)} />
                </div>
              </div>
              <div className='pac-form-row'>
                <div className='pac-form-group'>
                  <label>Status</label>
                  <select value={editForm.status} onChange={e => handleEditFormChange('status', e.target.value)}>
                    <option value='Published'>Published</option>
                    <option value='Draft'>Draft</option>
                    <option value='Disabled'>Disabled</option>
                  </select>
                </div>
                <div className='pac-form-group'>
                  <label>Active</label>
                  <select value={editForm.isActive} onChange={e => handleEditFormChange('isActive', e.target.value === 'true')}>
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </div>
              </div>
              <div className='pac-form-group'>
                <label>Allowed Vehicles (comma-separated or "all")</label>
                <input value={editForm.allowedVehicleNo} onChange={e => handleEditFormChange('allowedVehicleNo', e.target.value)} placeholder='all' />
              </div>
              <div className='pac-form-group'>
                <label>Client API URL</label>
                <input value={editForm['apiConfigs.clientApiUrl']} onChange={e => handleEditFormChange('apiConfigs.clientApiUrl', e.target.value)} />
              </div>
              <div className='pac-form-row'>
                <div className='pac-form-group'>
                  <label>API Method</label>
                  <select value={editForm['apiConfigs.clientApiMethod']} onChange={e => handleEditFormChange('apiConfigs.clientApiMethod', e.target.value)}>
                    <option value='POST'>POST</option>
                    <option value='PUT'>PUT</option>
                    <option value='PATCH'>PATCH</option>
                  </select>
                </div>
                <div className='pac-form-group'>
                  <label>Payload Head JSON String</label>
                  <input value={editForm['apiConfigs.clientApiPayloadHeadJsonString']} onChange={e => handleEditFormChange('apiConfigs.clientApiPayloadHeadJsonString', e.target.value)} />
                </div>
              </div>
              <div className='pac-form-row-3'>
                <div className='pac-form-group'>
                  <label>PH Key For DataPoints</label>
                  <input value={editForm['apiConfigs.phKeyForDataPointsMap']} onChange={e => handleEditFormChange('apiConfigs.phKeyForDataPointsMap', e.target.value)} />
                </div>
                <div className='pac-form-group'>
                  <label>Max Data Per Call</label>
                  <input type='number' value={editForm['apiConfigs.maxDataObjectPerAPICall']} onChange={e => handleEditFormChange('apiConfigs.maxDataObjectPerAPICall', Number(e.target.value))} />
                </div>
                <div className='pac-form-group'>
                  <label>Max Historic Per Call</label>
                  <input type='number' value={editForm['apiConfigs.maxHistoricDataObjectPerAPICall']} onChange={e => handleEditFormChange('apiConfigs.maxHistoricDataObjectPerAPICall', Number(e.target.value))} />
                </div>
              </div>
              <div className='pac-form-row-3'>
                <div className='pac-form-group'>
                  <label>Max Timestamp Old (days)</label>
                  <input type='number' value={editForm['apiConfigs.maxCanDataTimestampOldInDays']} onChange={e => handleEditFormChange('apiConfigs.maxCanDataTimestampOldInDays', Number(e.target.value))} />
                </div>
                <div className='pac-form-group'>
                  <label>PH Key For Count</label>
                  <input value={editForm['apiConfigs.phKeyForCount']} onChange={e => handleEditFormChange('apiConfigs.phKeyForCount', e.target.value)} />
                </div>
                <div className='pac-form-group'>
                  <label>&nbsp;</label>
                  <div style={{ display: 'flex', gap: 12, paddingTop: 4 }}>
                    <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type='checkbox' className='pac-checkbox' checked={editForm['apiConfigs.isDataPointsMapInsidePH']}
                        onChange={e => handleEditFormChange('apiConfigs.isDataPointsMapInsidePH', e.target.checked)} />
                      Map Inside PH
                    </label>
                    <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type='checkbox' className='pac-checkbox' checked={editForm['apiConfigs.isHistoricalDataReq']}
                        onChange={e => handleEditFormChange('apiConfigs.isHistoricalDataReq', e.target.checked)} />
                      Historical
                    </label>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type='checkbox' className='pac-checkbox' checked={editForm['apiConfigs.isCountIncludedInPH']}
                    onChange={e => handleEditFormChange('apiConfigs.isCountIncludedInPH', e.target.checked)} />
                  Count in PH
                </label>
                <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type='checkbox' className='pac-checkbox' checked={editForm['apiConfigs.resendFailedObjects']}
                    onChange={e => handleEditFormChange('apiConfigs.resendFailedObjects', e.target.checked)} />
                  Resend Failed
                </label>
                <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type='checkbox' className='pac-checkbox' checked={editForm['apiConfigs.nullifyCanPointsForCanNonComm']}
                    onChange={e => handleEditFormChange('apiConfigs.nullifyCanPointsForCanNonComm', e.target.checked)} />
                  Nullify CAN Non-Comm
                </label>
              </div>
            </div>
            <div className='pac-modal-footer'>
              <button className='pac-btn-cancel' onClick={() => setEditModal(null)}>Cancel</button>
              <button className='pac-btn-save' onClick={saveEditConfig}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* DataPointsMap Modal */}
      {dpmModal && (
        <div className='pac-modal-overlay' onClick={() => { setDpmModal(null); setNewDpmEntry(null); setEditDpmEntry(null) }}>
          <div className='pac-modal' onClick={e => e.stopPropagation()} style={{ maxWidth: 1000 }}>
            <div className='pac-modal-header'>
              <h6>Data Points Map - {dpmModal.clientCode} ({dpmModal.serviceId})</h6>
              <button className='pac-modal-close' onClick={() => { setDpmModal(null); setNewDpmEntry(null); setEditDpmEntry(null) }}>&times;</button>
            </div>
            <div className='pac-modal-body'>
              <div className='pac-dpm-section'>
                <div className='pac-dpm-header'>
                  <h6>{dpmModal.apiConfigs?.dataPointsMap?.length || 0} Data Points</h6>
                  {!newDpmEntry && (
                    <button className='pac-action-btn pac-btn-add' onClick={() => { setNewDpmEntry({ ...emptyDpmEntry }); setEditDpmEntry(null) }}>
                      + Add Data Point
                    </button>
                  )}
                </div>
                <div className='pac-table-wrapper'>
                  <Table bordered size='sm' className='pac-dpm-table'>
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Key (Source)</th>
                        <th>Value (Target)</th>
                        <th>Data Type</th>
                        <th>Value Range</th>
                        <th>Decimal Places</th>
                        <th>Mandatory</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dpmModal.apiConfigs?.dataPointsMap || []).map((entry, idx) => (
                        editDpmEntry && editDpmEntry.entryId === entry._id ? (
                          <tr key={entry._id} style={{ background: '#fffde7' }}>
                            <td>{idx + 1}</td>
                            <td><input value={editDpmEntry.key} onChange={e => setEditDpmEntry(p => ({ ...p, key: e.target.value }))} /></td>
                            <td><input value={editDpmEntry.value} onChange={e => setEditDpmEntry(p => ({ ...p, value: e.target.value }))} /></td>
                            <td>
                              <select value={editDpmEntry.dataType} onChange={e => setEditDpmEntry(p => ({ ...p, dataType: e.target.value }))}>
                                {DATA_TYPES.map(dt => <option key={dt} value={dt}>{dt}</option>)}
                              </select>
                            </td>
                            <td><input value={editDpmEntry.valueRange} onChange={e => setEditDpmEntry(p => ({ ...p, valueRange: e.target.value }))} placeholder='comma separated' /></td>
                            <td><input type='number' value={editDpmEntry.valueDecimalPlacesUpTo} onChange={e => setEditDpmEntry(p => ({ ...p, valueDecimalPlacesUpTo: e.target.value }))} style={{ width: 60 }} /></td>
                            <td style={{ textAlign: 'center' }}>
                              <input type='checkbox' checked={editDpmEntry.isMandatory} onChange={e => setEditDpmEntry(p => ({ ...p, isMandatory: e.target.checked }))} />
                            </td>
                            <td>
                              <div className='pac-dpm-inline-actions'>
                                <button className='pac-action-btn pac-btn-save' onClick={handleUpdateDpmEntry} style={{ fontSize: 10, padding: '2px 8px' }}>Save</button>
                                <button className='pac-action-btn pac-btn-cancel' onClick={() => setEditDpmEntry(null)} style={{ fontSize: 10, padding: '2px 8px' }}>Cancel</button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={entry._id}>
                            <td>{idx + 1}</td>
                            <td><strong>{entry.key}</strong></td>
                            <td>{entry.value}</td>
                            <td>{entry.dataType}</td>
                            <td>{(entry.valueRange || []).map((v, vi) => <span key={vi} className='pac-chip'>{v}</span>)}</td>
                            <td>{entry.valueDecimalPlacesUpTo}</td>
                            <td className={entry.isMandatory ? 'pac-mandatory-yes' : 'pac-mandatory-no'}>
                              {entry.isMandatory ? 'Yes' : 'No'}
                            </td>
                            <td>
                              <button className='pac-action-btn pac-btn-edit' onClick={() => startEditDpmRow(entry)} style={{ fontSize: 10, padding: '2px 8px' }}>Edit</button>
                            </td>
                          </tr>
                        )
                      ))}

                      {/* Add new row */}
                      {newDpmEntry && (
                        <tr style={{ background: '#e8f5e9' }}>
                          <td>+</td>
                          <td><input value={newDpmEntry.key} onChange={e => setNewDpmEntry(p => ({ ...p, key: e.target.value }))} placeholder='e.g. lat' /></td>
                          <td><input value={newDpmEntry.value} onChange={e => setNewDpmEntry(p => ({ ...p, value: e.target.value }))} placeholder='e.g. latitude' /></td>
                          <td>
                            <select value={newDpmEntry.dataType} onChange={e => setNewDpmEntry(p => ({ ...p, dataType: e.target.value }))}>
                              {DATA_TYPES.map(dt => <option key={dt} value={dt}>{dt}</option>)}
                            </select>
                          </td>
                          <td><input value={newDpmEntry.valueRange} onChange={e => setNewDpmEntry(p => ({ ...p, valueRange: e.target.value }))} placeholder='comma separated' /></td>
                          <td><input type='number' value={newDpmEntry.valueDecimalPlacesUpTo} onChange={e => setNewDpmEntry(p => ({ ...p, valueDecimalPlacesUpTo: e.target.value }))} style={{ width: 60 }} /></td>
                          <td style={{ textAlign: 'center' }}>
                            <input type='checkbox' checked={newDpmEntry.isMandatory} onChange={e => setNewDpmEntry(p => ({ ...p, isMandatory: e.target.checked }))} />
                          </td>
                          <td>
                            <div className='pac-dpm-inline-actions'>
                              <button className='pac-action-btn pac-btn-add' onClick={handleAddDpmEntry} style={{ fontSize: 10, padding: '2px 8px' }}>Add</button>
                              <button className='pac-action-btn pac-btn-cancel' onClick={() => setNewDpmEntry(null)} style={{ fontSize: 10, padding: '2px 8px' }}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PushApiConfig
